import { CSSProperties, ReactNode, forwardRef } from "react";
import { Input } from "antd";
import type { InputProps } from "antd";

type MarginValue = "none" | "dense" | "normal";

export type UIInputProps = Omit<InputProps, "size"> & {
  label?: ReactNode;
  fullWidth?: boolean;
  margin?: MarginValue;
  sx?: Record<string, unknown>;
  InputProps?: Record<string, unknown>;
  InputLabelProps?: Record<string, unknown>;
  variant?: string;
  size?: "small" | "middle" | "large";
  noWrapper?: boolean;
  forceNative?: boolean;
};

const toInputSize = (size?: "small" | "middle" | "large") => {
  if (size === "small") return "small";
  if (size === "large") return "large";
  return "middle";
};

const toWrapperStyle = (
  style?: CSSProperties,
  sx?: Record<string, unknown>,
  fullWidth?: boolean,
  margin?: MarginValue
): CSSProperties => {
  const sxStyle = (sx ?? {}) as CSSProperties;
  return {
    ...(fullWidth ? { width: "100%" } : {}),
    ...(margin === "normal" ? { marginBottom: 16 } : {}),
    ...sxStyle,
    ...style,
  };
};

const pickVisualInputStyle = (style?: CSSProperties): CSSProperties => {
  if (!style) return {};
  return {
    border: style.border,
    borderColor: style.borderColor,
    borderWidth: style.borderWidth,
    borderRadius: style.borderRadius,
    background: style.background,
    backgroundColor: style.backgroundColor,
    boxShadow: style.boxShadow,
    color: style.color,
  };
};

export const UIInput = forwardRef<any, UIInputProps>(
  (
    {
      label,
      fullWidth,
      margin,
      sx,
      style,
      size,
      InputProps,
      InputLabelProps,
      variant,
      noWrapper,
      forceNative,
      ...props
    }: UIInputProps,
    ref
  ) => {
    const wrapperStyle = toWrapperStyle(style, sx, fullWidth, margin);
    const labelStyle = (InputLabelProps?.sx ?? {}) as CSSProperties;
    const inputStyle = (InputProps?.sx ?? {}) as CSSProperties;
    const visualStyleFromWrapper = pickVisualInputStyle(style as CSSProperties);
    const directInputStyle: CSSProperties = {
      ...(sx as CSSProperties),
      ...(style as CSSProperties),
      ...inputStyle,
    };
    const shouldUseNativeInput =
      forceNative || props.type === "file";

    if (noWrapper) {
      if (shouldUseNativeInput) {
        return (
          <input
            ref={ref}
            style={directInputStyle}
            {...(props as any)}
          />
        );
      }

      return (
        <Input
          ref={ref}
          size={toInputSize(size)}
          style={directInputStyle}
          {...props}
        />
      );
    }

    if (shouldUseNativeInput) {
      return (
        <div style={wrapperStyle}>
          {label ? (
            <label style={{ display: "block", marginBottom: 6, ...labelStyle }}>
              {label}
            </label>
          ) : null}
          <input
            ref={ref}
            style={{ width: "100%", ...inputStyle }}
            {...(props as any)}
          />
        </div>
      );
    }

    return (
      <div style={wrapperStyle}>
        {label ? (
          <label style={{ display: "block", marginBottom: 6, ...labelStyle }}>
            {label}
          </label>
        ) : null}
        <Input
          ref={ref}
          size={toInputSize(size)}
          style={{ ...visualStyleFromWrapper, ...inputStyle }}
          {...props}
        />
      </div>
    );
  }
);

UIInput.displayName = "UIInput";
